import React, { useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";

import { formatMeterName } from "../../../utils/proposalDocuments/relatedMeters";

import {
  BulkCustomer,
  isMeterUsable,
  MAX_OFFERS_PER_METER,
  MIN_OFFERS_PER_METER,
  uploadCustomerImage,
} from "./bulk.utils";

interface BulkCustomerCardProps {
  customer: BulkCustomer;
  onChange: (customer: BulkCustomer) => void;
  onRemove: () => void;
}

const BulkCustomerCard: React.FC<BulkCustomerCardProps> = ({ customer, onChange, onRemove }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const usableIds = customer.meters.filter(isMeterUsable).map((m) => m.id);
  const nameMissing = !customer.customerName.trim();
  const noneSelected = customer.selectedMeterIds.length === 0;

  const toggleMeter = (meterId: string) => {
    const selected = customer.selectedMeterIds.includes(meterId)
      ? customer.selectedMeterIds.filter((id) => id !== meterId)
      : [...customer.selectedMeterIds, meterId];
    onChange({ ...customer, selectedMeterIds: selected });
  };

  const handleOffersChange = (value: string) => {
    const parsed = Number.parseInt(value, 10);
    if (Number.isNaN(parsed)) return;
    const clamped = Math.min(MAX_OFFERS_PER_METER, Math.max(MIN_OFFERS_PER_METER, parsed));
    onChange({ ...customer, offersPerMeter: clamped });
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      onChange({ ...customer, customerImage: null });
      return;
    }

    setIsUploading(true);
    setUploadError("");
    try {
      const s3Key = await uploadCustomerImage(file);
      onChange({ ...customer, customerImage: s3Key });
    } catch (err) {
      console.error("Bulk customer image upload failed:", err);
      setUploadError("Image upload failed - please try again.");
      onChange({ ...customer, customerImage: null });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span>
          <strong>{customer.customerName || "Unnamed customer"}</strong>{" "}
          <small className="text-muted">
            {customer.zohoId} - {customer.selectedMeterIds.length}/{customer.meters.length} meters
            selected
          </small>
        </span>
        <Button variant="outline-danger" size="sm" onClick={onRemove}>
          Remove
        </Button>
      </Card.Header>
      <Card.Body>
        <Row className="g-3 mb-3">
          <Col md={5}>
            <Form.Group controlId={`bulk-name-${customer.zohoId}`}>
              <Form.Label>Customer name</Form.Label>
              <Form.Control
                type="text"
                value={customer.customerName}
                isInvalid={nameMissing}
                onChange={(e) => {
                  onChange({ ...customer, customerName: e.target.value });
                }}
              />
              <Form.Control.Feedback type="invalid">
                A customer name is required
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group controlId={`bulk-offers-${customer.zohoId}`}>
              <Form.Label>Offers per meter</Form.Label>
              <Form.Control
                type="number"
                min={MIN_OFFERS_PER_METER}
                max={MAX_OFFERS_PER_METER}
                value={customer.offersPerMeter}
                onChange={(e) => {
                  handleOffersChange(e.target.value);
                }}
              />
            </Form.Group>
          </Col>
          <Col md={5}>
            <Form.Group controlId={`bulk-image-${customer.zohoId}`}>
              <Form.Label>Customer image (optional)</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                disabled={isUploading}
                isInvalid={Boolean(uploadError)}
                onChange={(e) => void handleImageChange(e as React.ChangeEvent<HTMLInputElement>)}
              />
              <Form.Text className="text-muted">
                {isUploading ? "Uploading..." : customer.customerImage ? "Image uploaded" : ""}
              </Form.Text>
              <Form.Control.Feedback type="invalid">{uploadError}</Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <div className="d-flex align-items-center gap-2 mb-2">
          <strong>Meters</strong>
          <small className="text-muted">
            ({customer.selectedMeterIds.length} of {usableIds.length} selected)
          </small>
          <Button
            variant="link"
            size="sm"
            onClick={() => {
              onChange({ ...customer, selectedMeterIds: usableIds });
            }}
          >
            Select all
          </Button>
          <Button
            variant="link"
            size="sm"
            onClick={() => {
              onChange({ ...customer, selectedMeterIds: [] });
            }}
          >
            Select none
          </Button>
        </div>

        {customer.meters.map((meter) => {
          const usable = isMeterUsable(meter);
          const reference = meter.mpan
            ? `MPAN ${meter.mpan}`
            : meter.mprn
              ? `MPRN ${meter.mprn}`
              : "";
          return (
            <Form.Check
              key={meter.id}
              id={`bulk-meter-${customer.zohoId}-${meter.id}`}
              type="checkbox"
              disabled={!usable}
              checked={usable && customer.selectedMeterIds.includes(meter.id)}
              onChange={() => {
                toggleMeter(meter.id);
              }}
              label={
                <>
                  {formatMeterName(meter.meterName)}{" "}
                  <small className="text-muted">
                    {usable
                      ? reference
                      : meter.mpan && meter.mprn
                        ? "(has both an MPAN and MPRN - fix in Zoho to include)"
                        : "(no MPAN or MPRN - fix in Zoho to include)"}
                  </small>
                </>
              }
            />
          );
        })}

        {noneSelected && (
          <small className="text-danger">
            No meters selected - this customer won&apos;t be included in the template
          </small>
        )}
      </Card.Body>
    </Card>
  );
};

export default BulkCustomerCard;
